import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Recycle,
  Leaf,
  PiggyBank,
  CheckCircle2,
  Tag,
  Wrench,
  Layers,
  Sparkles,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city, products = [] }) => {
  const recoStandard = products.find(
    (p) => p.slug === "carton-standard-reconditionne",
  );
  const recoLivres = products.find(
    (p) => p.slug === "carton-livres-reconditionne",
  );

  return (
    <>
      {/* ======= SECTION 2 — Livraison ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison au Plessis-Robinson : rapidité et zéro intermédiaire
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Ne perdez plus votre temps dans les magasins de bricolage ou
              les zones commerciales, et évitez de surcharger votre
              véhicule avec des liasses encombrantes. Nous gérons
              l&apos;acheminement de vos fournitures directement chez vous
              au Plessis-Robinson.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Flotte de livraison 100% interne",
                body: "C'est notre propre société qui prend en charge vos colis en Île-de-France. Vos cartons ne passent par aucun transporteur externe, ce qui garantit qu'ils ne seront ni perdus ni détériorés.",
                Icon: Truck,
              },
              {
                title: "Garantie de rigidité",
                body: "Nos véhicules fermés protègent le matériel contre l'humidité et la poussière. Vous recevez des fournitures impeccables, sèches et prêtes pour l'assemblage.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Reconditionnés -40% (promo hero verte) ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6] relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#237cb5]/15 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-[#FF7A00]/10 blur-3xl"
        />
        <div className="container-cdm relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#237cb5]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
              <Leaf className="h-3.5 w-3.5" strokeWidth={2.6} />
              Alternative écoresponsable
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Cartons reconditionnés jusqu&apos;à
              <span className="ml-2 inline-block px-3 py-1 rounded-xl bg-[#FF7A00] text-white">
                −40&nbsp;%
              </span>
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Allier protection de la planète et économies sur votre
              budget, c&apos;est possible. Nous vous proposons une gamme
              exclusive de cartons de déménagement de seconde main, triés
              sur le volet et affichant des tarifs imbattables.
            </p>
          </div>

          {/* 3 benefit cards */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Un prix ultra-compétitif",
                body: "En choisissant nos modèles réutilisés, vous bénéficiez d'une réduction allant jusqu'à -40% par rapport au prix d'un produit neuf, sans faire l'impasse sur la performance.",
                Icon: PiggyBank,
              },
              {
                title: "Une sélection drastique",
                body: "Chaque carton reconditionné est inspecté manuellement par nos équipes. Nous validons sa géométrie, l'intégrité de ses rabats et la solidité de sa cannelure pour vous garantir une résistance à l'empilement identique à celle d'un emballage sorti d'usine.",
                Icon: CheckCircle2,
              },
              {
                title: "Un geste concret pour la planète",
                body: "Donner une seconde vie à ces contenants permet de prolonger le cycle de vie de la fibre de papier et de réduire drastiquement l'empreinte carbone globale de votre déménagement.",
                Icon: Recycle,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>

          {/* Featured 2 reconditioned products */}
          {(recoStandard || recoLivres) && (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[recoStandard, recoLivres].filter(Boolean).map((p) => (
                <Link
                  key={p.id}
                  to={`/produit/${p.slug}`}
                  className="group flex items-center justify-between bg-white hover:shadow-[0_20px_45px_-20px_rgba(35,124,181,0.45)] border border-[#cfdbe6] hover:border-[#237cb5] rounded-2xl p-5 md:p-6 transition-all"
                  data-testid={`city-plessis-cta-${p.slug}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="flex items-center justify-center h-14 w-14 rounded-2xl bg-[#237cb5] text-white">
                      <Recycle className="h-7 w-7" strokeWidth={2.4} />
                    </span>
                    <div>
                      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FF7A00] text-white text-[10.5px] uppercase tracking-[0.14em] font-bold mb-1">
                        Seconde vie
                      </div>
                      <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                        {p.name}
                      </h3>
                      <div className="mt-1 font-display font-extrabold text-[18px] text-[#237cb5]">
                        {Number(p.price).toFixed(2).replace(".", ",")} €
                      </div>
                    </div>
                  </div>
                  <ArrowRight
                    className="h-5 w-5 text-[#237cb5] group-hover:translate-x-1 transition-transform shrink-0"
                    strokeWidth={2.6}
                  />
                </Link>
              ))}
            </div>
          )}

          <div className="mt-10">
            <Link
              to="/cartons-reconditionnes"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-plessis-cta-recyclables"
            >
              <Leaf className="h-4 w-4" strokeWidth={2.6} />
              Voir toute la gamme reconditionnée
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Accessoires de fermeture : 3 produits scellage ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
              Accessoires essentiels
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Bloquez les risques&nbsp;: nos accessoires de fermeture
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Un bon carton n&apos;est rien sans un système de scellage
              performant. Pour sécuriser vos fermetures et renforcer la
              structure de vos colis, voici les accessoires indispensables
              à retrouver sur notre boutique.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                tag: "L'ancre structurelle",
                title: "Le ruban adhésif haute cohésion",
                body: "Conçu pour fusionner instantanément avec la fibre du carton, il garantit que le fond de vos boîtes ne cédera pas sous le poids de vos affaires, même lors des manutentions intensives.",
                Icon: Tag,
                to: "/produit/rouleau-adhesif",
                cta: "Voir le rouleau d'adhésif",
                testId: "city-plessis-cta-adhesif",
              },
              {
                tag: "Le gain de temps",
                title: "Le dévidoir manuel ergonomique",
                body: "Cet outil professionnel vous permet de couper et d'appliquer votre adhésif d'un seul geste fluide. Il assure une tension parfaite du scotch pour une fermeture totalement hermétique en un temps record.",
                Icon: Wrench,
                to: "/produit/devidoir-pistolet-adhesif",
                cta: "Voir le dévidoir",
                testId: "city-plessis-cta-devidoir",
              },
              {
                tag: "Le bouclier externe",
                title: "Le film étirable technique",
                body: "Idéal pour lier plusieurs colis ensemble, maintenir les portes de vos petits meubles fermées ou protéger vos cartons de la pluie pendant le portage. Il agit comme une seconde peau protectrice.",
                Icon: Layers,
                to: "/produit/film-etirable",
                cta: "Voir le film étirable",
                testId: "city-plessis-cta-film",
              },
            ].map(({ tag, title, body, Icon, to, cta, testId }) => (
              <Link
                key={title}
                to={to}
                className="group block bg-[#EEF3F7] hover:bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] rounded-2xl p-5 md:p-6 transition-all"
                data-testid={testId}
              >
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#cfdbe6] text-[10.5px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                    {tag}
                  </div>
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                </div>
                <h3 className="mt-4 font-display font-extrabold text-[16.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] group-hover:gap-2.5 transition-all">
                  {cta}
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-plessis-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
